import React, { useState } from "react";
import api from "../../utils/axiosConfig";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { toast } from "react-toastify";



// STYLED BUTTON
const OrderButton = styled.button`
  background-color: #38a169;
  color: white;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  margin-top: 20px;
  transition: background-color 0.3s ease, transform 0.2s ease;

  &:hover {
    background-color: #2f855a;
    transform: translateY(-1px);
  }

  &:disabled {
    background-color: #a0aec0;
    cursor: not-allowed;
    transform: none;
  }
`;

export default function Commande({
  selectedItems,
  totalWithPromo,
  promoData,
  promoCode,
  onSuccess,
}) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const handleOrder = async () => {
    if (!selectedItems || selectedItems.length === 0) {
      toast.error("Aucun article sélectionné !", {
        position: "top-right",
        autoClose: 4000,
        style: {
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: "14px",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(239, 68, 68, 0.3)",
          color: "#2c1810",
        }
      });
      return;
    }

    // 🔍 FILTRER LES ARTICLES INVALIDES (produits null)
    const validItems = selectedItems.filter(item => {
      if (!item.product || !item.product._id) {
        console.warn("⚠️ Article invalide détecté:", item);
        return false;
      }
      return true;
    });

    if (validItems.length === 0) {
      toast.error("❌ Tous les articles sélectionnés sont invalides. Veuillez rafraîchir votre panier.", {
        position: "top-right",
        autoClose: 6000,
        style: {
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: "14px",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(239, 68, 68, 0.3)",
          color: "#2c1810",
        }
      });
      return;
    }

    // Afficher un avertissement si certains articles ont été filtrés
    if (validItems.length < selectedItems.length) {
      toast.warning(`⚠️ ${selectedItems.length - validItems.length} article(s) invalide(s) ignoré(s). Commande en cours avec ${validItems.length} article(s) valide(s).`, {
        position: "top-right",
        autoClose: 5000,
        style: {
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: "14px",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(245, 158, 11, 0.3)",
          color: "#2c1810",
        }
      });
    }

    setIsLoading(true);

    try {
      // Log des données pour déboguer
      console.log("🔍 Données de commande:", {
        selectedItems,
        validItems,
        totalWithPromo,
        promoData,
        promoCode
      });

      // Vérification des données avant envoi
      if (!totalWithPromo || totalWithPromo <= 0) {
        throw new Error("Total invalide");
      }

      console.log("🔍 Vérification des données de commande:", {
        totalWithPromo: totalWithPromo.toFixed(2),
        promoData: promoData,
        promoCode: promoCode,
        itemsCount: validItems.length
      });

      // Préparer les données de la commande avec les articles valides uniquement
      const orderData = {
        items: validItems.map((item) => {
          if (!item.product?._id || !item.couleur || !item.taille || !item.quantity) {
            throw new Error(`Données d'article invalides: ${JSON.stringify(item)}`);
          }
          return {
            productId: item.product._id,
            couleur: item.couleur,
            taille: item.taille,
            quantity: item.quantity,
          };
        }),
        total: totalWithPromo,
        promo: promoData && promoCode ? {
          code: promoCode,
          pourcentage: promoData.pourcentage,
        } : null,
      };

      console.log("📦 Données envoyées au serveur:", {
        ...orderData,
        total: orderData.total.toFixed(2),
        promoApplied: orderData.promo ? `${orderData.promo.pourcentage}%` : "Aucune"
      });

      // 1) Créer la commande avec les articles + total + promo
      const response = await api.post(
        `/api/orders`,
        orderData,
        {
          timeout: 30000, // Timeout de 30 secondes
        }
      );

      console.log("✅ Réponse du serveur:", response.data);

      // Toast de succès avec style simple
      toast.success("✅ Commande passée avec succès !", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: "14px",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(34, 197, 94, 0.3)",
          color: "#2c1810",
        }
      });

      // 2) Supprimer les articles commandés du panier (uniquement les valides)
      try {
        const removeResponse = await api.post(
          `/api/shop/cart/remove-multiple`,
          {
            items: validItems.map((item) => ({
              productId: item.product._id,
              couleur: item.couleur,
              taille: item.taille,
            })),
          },
          {
            timeout: 10000, // Timeout de 10 secondes
          }
        );

        console.log("🗑️ Articles supprimés du panier:", removeResponse.data);
      } catch (removeError) {
        console.error("⚠️ Erreur lors de la suppression du panier:", removeError);
        // Ne pas bloquer la commande si la suppression du panier échoue
        toast.warning("Commande créée mais erreur lors de la suppression du panier", {
          position: "top-right",
          autoClose: 4000,
          style: {
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "14px",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(245, 158, 11, 0.3)",
            color: "#2c1810",
          }
        });
      }

      if (onSuccess) onSuccess(response.data.order);

      // Rediriger vers le profil après un délai
      setTimeout(() => {
        navigate("/profile");
      }, 1000);

    } catch (err) {
      console.error("❌ Erreur détaillée de commande:", err);
      
      // Message d'erreur plus détaillé
      let errorMessage = "❌ Erreur lors de la commande.";
      
      if (err.response) {
        // Erreur de réponse du serveur
        const serverError = err.response.data?.message || err.response.data?.error;
        if (serverError) {
          errorMessage = `❌ Erreur serveur: ${serverError}`;
        }
        
        if (err.response.status === 401) {
          errorMessage = "❌ Vous devez être connecté pour passer une commande.";
        } else if (err.response.status === 400) {
          errorMessage = `❌ Données invalides: ${serverError}`;
        } else if (err.response.status === 404) {
          errorMessage = "❌ Produit introuvable. Veuillez rafraîchir la page.";
        } else if (err.response.status === 500) {
          errorMessage = "❌ Erreur interne du serveur. Veuillez réessayer.";
        }
      } else if (err.request) {
        // Erreur de réseau
        if (err.code === 'ECONNABORTED') {
          errorMessage = "❌ Délai d'attente dépassé. Vérifiez votre connexion.";
        } else {
          errorMessage = "❌ Erreur de connexion au serveur. Vérifiez votre connexion.";
        }
      } else if (err.message) {
        // Erreur personnalisée
        errorMessage = `❌ ${err.message}`;
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: "14px",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(239, 68, 68, 0.3)",
          color: "#2c1810",
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <OrderButton 
        onClick={handleOrder} 
        disabled={selectedItems.length === 0 || isLoading}
      >
        {isLoading ? "Création de la commande..." : "Commander les articles sélectionnés"}
      </OrderButton>
    </>
  );
}
